import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map, from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResponseWrapper } from '../../models/api/response-wrapper.model';
import { AuthStateService } from '../auth/auth-state.service';

@Injectable({
  providedIn: 'root',
})
export class HttpGenericService {
  private readonly baseUrl = environment.KONG_SERVICE_URL;
  private readonly apikey = environment.APIKEY;
  private readonly http = inject(HttpClient);
  private readonly authStateService = inject(AuthStateService);

  // --- STANDARD METHODS (Return T) ---

  get<T>(route: string, params?: HttpParams, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => this.http.get<ResponseWrapper<T>>(url, { params, headers })),
      map((response) => response.data),
    );
  }

  getPublic<T>(route: string, params?: HttpParams, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    const headers = new HttpHeaders({
      apikey: this.apikey,
    });
    return this.http.get<ResponseWrapper<T>>(url, { params, headers }).pipe(map((response) => response.data));
  }

  post<T, B = any>(route: string, body: B, customUrl?: string, customHeaders?: HttpHeaders): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    if (customHeaders) {
      let headers = customHeaders;
      if (!headers.has('apikey')) {
        headers = headers.set('apikey', this.apikey);
      }

      if (body instanceof FormData) {
        headers = headers.delete('Content-Type');
      }

      return this.http.post<ResponseWrapper<T>>(url, body, { headers }).pipe(map((res) => res.data));
    }
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        let finalHeaders = headers;
        if (body instanceof FormData) {
          finalHeaders = headers.delete('Content-Type');
        }
        return this.http.post<ResponseWrapper<T>>(url, body, { headers: finalHeaders });
      }),
      map((res) => res.data),
    );
  }

  postPublic<T, B = any>(route: string, body: B, customUrl?: string, customHeaders?: HttpHeaders): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    console.log('URL de la petición:', url);
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.apikey,
    });
    console.log(body);
    if (customHeaders) {
      customHeaders.keys().forEach((key) => {
        const value = customHeaders.get(key);

        if (value !== null) {
          headers = headers.set(key, value);
        }
      });
    }

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    console.log('URL:', url);
    console.log(
      'Headers:',
      headers.keys().map((key) => ({
        key,
        value: headers.get(key),
      })),
    );
    console.log(headers);
    return this.http.post<ResponseWrapper<T>>(url, body, { headers }).pipe(
      map((response) => {
        console.log(response);
        return response.data;
      }),
    );
  }

  /**
   * Variante de POST que retorna la respuesta completa del backend sin headers de autenticación.
   * Útil para endpoints públicos como /login/auth donde se requiere inspeccionar el objeto error.
   */
  postFullPublic<T, B = any>(
    route: string,
    body: B,
    customUrl?: string,
    customHeaders?: HttpHeaders,
  ): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.apikey, // o 'apikey' según contrato del API
    });

    if (customHeaders) {
      customHeaders.keys().forEach((key) => {
        const value = customHeaders.get(key);
        if (value !== null) {
          headers = headers.set(key, value);
        }
      });
    }

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this.http.post<ResponseWrapper<T>>(url, body, { headers });
  }

  /**
   * POST hacia un servicio externo cuya respuesta NO viene envuelta en `ResponseWrapper`;
   * retorna el cuerpo tal cual lo emite el proveedor.
   *
   * No adjunta la `apikey` interna de forma deliberada: esa credencial pertenece al
   * gateway y no debe viajar a servicios fuera de él. Pase `customHeaders` si el
   * proveedor externo exige su propia autenticación.
   */
  postExternal<T, B = unknown>(url: string, body: B, customHeaders?: HttpHeaders): Observable<T> {
    let headers = customHeaders ?? new HttpHeaders({ 'Content-Type': 'application/json' });

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this.http.post<T>(url, body, { headers });
  }

  put<T, B = any>(route: string, body: B, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        let finalHeaders = headers;
        if (body instanceof FormData) {
          finalHeaders = headers.delete('Content-Type');
        }
        return this.http.put<ResponseWrapper<T>>(url, body, { headers: finalHeaders });
      }),
      map((res) => res.data),
    );
  }

  patch<T, B = any>(route: string, body?: B, params?: HttpParams, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        let finalHeaders = headers;
        if (body instanceof FormData) {
          finalHeaders = headers.delete('Content-Type');
        }
        return this.http.request<ResponseWrapper<T>>('PATCH', url, {
          headers: finalHeaders,
          body,
          params,
        });
      }),
      map((res) => (res as any).data as T),
    );
  }

  delete<T>(route: string, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => this.http.delete<ResponseWrapper<T>>(url, { headers })),
      map((res) => res.data),
    );
  }

  // DELETE con body (útil para endpoints que requieren payload en DELETE)
  deleteWithBody<T, B = any>(route: string, body: B, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => this.http.request<ResponseWrapper<T>>('DELETE', url, { headers, body })),
      map((res) => (res as any).data as T),
    );
  }

  // --- FULL METHODS (Return ResponseWrapper<T>) ---

  /**
   * Variante de GET que retorna la respuesta completa del backend
   * sin desestructurar `data`. Úsese cuando se requiere inspeccionar
   * `success`/`error` además de `data`.
   */
  getFull<T>(route: string, params?: HttpParams, customUrl?: string): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => this.http.get<ResponseWrapper<T>>(url, { params, headers })),
    );
  }

  /**
   * Variante de POST que retorna la respuesta completa del backend
   */
  postFull<T, B = any>(route: string, body: B, customUrl?: string): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        let finalHeaders = headers;
        if (body instanceof FormData) {
          finalHeaders = headers.delete('Content-Type');
        }
        return this.http.post<ResponseWrapper<T>>(url, body, { headers: finalHeaders });
      }),
    );
  }

  /**
   * Variante de PATCH que retorna la respuesta completa del backend
   */
  patchFull<T, B = any>(
    route: string,
    body?: B,
    params?: HttpParams,
    customUrl?: string,
  ): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        let finalHeaders = headers;
        if (body instanceof FormData) {
          finalHeaders = headers.delete('Content-Type');
        }
        return this.http.request<ResponseWrapper<T>>('PATCH', url, {
          headers: finalHeaders,
          body,
          params,
        });
      }),
    );
  }

  patchFullPublic<T, B = any>(
    route: string,
    body?: B,
    params?: HttpParams,
    customUrl?: string,
  ): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.apikey,
    });

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this.http.request<ResponseWrapper<T>>('PATCH', url, {
      body,
      params,
      headers,
    });
  }

  postBlob<T = Blob, B = any>(route: string, body: B, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<T>(url, body, {
          headers,
          responseType: 'blob' as 'json',
        }),
      ),
    );
  }

  /** GET que retorna Blob con autenticación */
  getBlob(route: string, params?: HttpParams, customUrl?: string): Observable<Blob> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.get(url, {
          params,
          headers,
          responseType: 'blob' as 'json',
        }),
      ),
    ) as Observable<Blob>;
  }

  /**
   * Envía un POST multipart/form-data. No fija el Content-Type para permitir que el navegador asigne el boundary.
   * Incluye automáticamente los encabezados de autenticación y el refresh del token si es necesario.
   */
  postFormData<T>(route: string, form: FormData, customUrl?: string): Observable<T> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        const safeHeaders = headers.delete('Content-Type');
        return this.http.post<ResponseWrapper<T>>(url, form, { headers: safeHeaders });
      }),
      map((res) => res.data),
    );
  }

  /**
   * Variante de postFormData que retorna la respuesta completa del backend
   */
  postFullFormData<T>(route: string, form: FormData, customUrl?: string): Observable<ResponseWrapper<T>> {
    const url = this.buildUrl(route, customUrl);
    return from(this.buildAuthHeaders()).pipe(
      switchMap((headers) => {
        const safeHeaders = headers.delete('Content-Type');
        return this.http.post<ResponseWrapper<T>>(url, form, { headers: safeHeaders });
      }),
    );
  }

  private buildUrl(route: string, base?: string): string {
    const cleanRoute = route.replace(/^\/+/, '');
    const cleanBase = (base ?? this.baseUrl).replace(/\/+$/, '');
    return `${cleanBase}/${cleanRoute}`;
  }

  private async buildAuthHeaders(): Promise<HttpHeaders> {
    // El Authorization se gestionará principalmente en el HttpInterceptor.
    // Este método sólo agrega Content-Type y, si hay token, lo adjunta (respaldo).
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.apikey,
    });
    try {
      const access = this.authStateService?.tokenAcceso;

      if (access) {
        headers = headers.set('Authorization', `Bearer ${access}`);
      }
    } catch (error) {
      console.error('Error al construir encabezados de autenticación:', error);
    }
    return headers;
  }
}
